import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { generatePath, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import {
  EmptyTableState,
  Table,
  TableData,
  TableHead,
  TableHeader,
} from "~/common/components";
import { Patient } from "~/common/utils";
import { ROUTES } from "~/providers/router";
import { consultationRequestsTableColumns } from "../consultationRequestsTable/consultationRequestsTableColumns";
import { CONSULTATION_REQUESTS_TABLE_COLUMN_IDS } from "../consultationRequestsTable/consultationRequestsTableSorting";
import { useConsultationRequestsTableSorting } from "../consultationRequestsTable/useConsultationRequestsTableSorting";
import { SortableTableHeader } from "../patientsTable/SortableTableHeader";

interface ConsultationRequestsTableProps {
  emptyDataForSearchCriteria: boolean;
  hasConsultations: boolean;
  consultations?: Patient[];
  isFetching?: boolean;
}

export const ConsultationRequestsTable = ({
  consultations,
  emptyDataForSearchCriteria,
  hasConsultations,
  isFetching = false,
}: ConsultationRequestsTableProps) => {
  const navigate = useNavigate();
  const { sorting, onSortingChange } = useConsultationRequestsTableSorting();

  const noConsultations = !consultations?.length;

  const table = useReactTable({
    data: consultations ?? [],
    columns: consultationRequestsTableColumns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    onSortingChange,
    state: { sorting },
  });

  const handleRowClick = (patientId: string) =>
    navigate(
      generatePath(ROUTES.consultationPreview, {
        patientId,
      }),
    );

  return (
    <div
      className={twMerge(
        "w-full overflow-x-auto rounded-xl border border-gray-200",
        noConsultations && "h-table",
      )}
    >
      <Table className="size-full table-auto">
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="w-full">
              {headerGroup.headers.map((header) => {
                const isActionsColumn =
                  header.column.id ===
                  CONSULTATION_REQUESTS_TABLE_COLUMN_IDS.actions;

                if (isActionsColumn) {
                  return <TableHeader key={header.id} className="w-0" />;
                }

                return (
                  <SortableTableHeader
                    key={header.id}
                    header={header}
                    className="flex-1"
                  />
                );
              })}
            </tr>
          ))}
        </TableHead>
        <tbody className="bg-white text-sm text-gray-70">
          {emptyDataForSearchCriteria && (
            <EmptyTableState
              colSpan={consultationRequestsTableColumns.length}
              message={
                hasConsultations
                  ? "No consultations match your search criteria."
                  : "There aren't any consultations requests yet."
              }
            />
          )}
          {table.getRowModel().rows.map((row) => {
            const unreviewed =
              !row.original.organization_patients?.[0].is_reviewed;

            return (
              <tr
                key={row.id}
                className={twMerge(
                  "transition hover:cursor-pointer hover:bg-gray-50",
                  unreviewed && "font-medium",
                  isFetching && "opacity-75",
                )}
                onClick={() => handleRowClick(row.original.id)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableData key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableData>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
